import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Plus, Pencil, Trash2, Eye, ArrowLeft } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { SkeletonTable } from '../components/ui/Skeleton';
import { ErrorState } from '../components/ui/EmptyState';
import AddActionModal from '../components/scheduled-actions/AddActionModal';
import EditActionModal from '../components/scheduled-actions/EditActionModal';
import DeleteActionModal from '../components/scheduled-actions/DeleteActionModal';
import ViewActionRunsModal from '../components/scheduled-actions/ViewActionRunsModal';
import { useActionsByService } from '../hooks/useScheduledActions';
import { useAllServices } from '../hooks/useServices';
import { formatDate } from '../utils/dateUtils';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const actionTypeBadge = {
    create_invoice: 'bg-accent/10 text-accent',
    create_reading: 'bg-category-electricity/10 text-category-electricity',
};

const ServiceActions = () => {
    const { uid } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { data: actions = [], isLoading: loading, error, refetch } = useActionsByService(uid);
    const { data: allServices = [] } = useAllServices();
    const service = allServices.find((s) => s.uid === uid);

    const typeLabels = {
        electricity: t(KEYS.TYPE_ELECTRICITY),
        natural_gas: t(KEYS.TYPE_NATURAL_GAS),
        water: t(KEYS.TYPE_WATER),
        phone: t(KEYS.TYPE_PHONE),
        internet: t(KEYS.TYPE_INTERNET),
        other: t(KEYS.TYPE_OTHER),
    };
    const actionTypeLabels = {
        create_invoice: t(KEYS.SERVICE_ACTIONS_TYPE_CREATE_INVOICE),
        create_reading: t(KEYS.SERVICE_ACTIONS_TYPE_CREATE_READING),
    };
    const frequencyLabels = {
        once: t(KEYS.SERVICE_ACTIONS_FREQ_ONCE),
        daily: t(KEYS.SERVICE_ACTIONS_FREQ_DAILY),
        weekly: t(KEYS.SERVICE_ACTIONS_FREQ_WEEKLY),
        monthly: t(KEYS.SERVICE_ACTIONS_FREQ_MONTHLY),
        yearly: t(KEYS.SERVICE_ACTIONS_FREQ_YEARLY),
    };

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRunsModalOpen, setIsRunsModalOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);

    const handleEdit = (action) => { setSelectedAction(action); setIsEditModalOpen(true); };
    const handleDelete = (action) => { setSelectedAction(action); setIsDeleteModalOpen(true); };
    const handleViewRuns = (action) => { setSelectedAction(action); setIsRunsModalOpen(true); };

    const serviceTitle = service ? (service.name || typeLabels[service.type] || service.type) : 'Service';

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(`/services/${uid}`)}
                        className="p-1.5 rounded-md text-text-muted hover:text-accent hover:bg-surface-overlay transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-semibold text-text-primary">{t(KEYS.SERVICE_ACTIONS_TITLE)}</h1>
                        <p className="text-sm text-text-muted mt-0.5">{serviceTitle}</p>
                    </div>
                </div>
                <Button
                    variant="primary"
                    size="small"
                    onClick={() => setIsAddModalOpen(true)}
                    className="gap-2"
                >
                    <Plus className="w-3.5 h-3.5" />
                    {t(KEYS.SERVICE_ACTIONS_ADD)}
                </Button>
            </div>

            {/* Content */}
            {loading ? (
                <SkeletonTable rows={5} columns={5} />
            ) : error ? (
                <ErrorState message={error?.message || t(KEYS.SERVICE_ACTIONS_ERROR_LOAD)} onRetry={refetch} />
            ) : actions.length > 0 ? (
                <Card padding="none" className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-surface-border">
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">{t(KEYS.SERVICE_ACTIONS_COL_TYPE)}</th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden sm:table-cell">{t(KEYS.SERVICE_ACTIONS_COL_FREQUENCY)}</th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">{t(KEYS.SERVICE_ACTIONS_COL_NEXT_RUN)}</th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden md:table-cell">{t(KEYS.SERVICE_ACTIONS_COL_LAST_RUN)}</th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">{t(KEYS.SERVICE_ACTIONS_COL_ACTIVE)}</th>
                                    <th className="px-4 py-2.5 w-24"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-border">
                                {actions.map((action, index) => (
                                    <tr
                                        key={action.uid}
                                        className="hover:bg-surface-overlay transition-colors group animate-appear"
                                        style={{ animationDelay: `${index * 30}ms` }}
                                    >
                                        <td className="px-4 py-2.5">
                                            <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${actionTypeBadge[action.type] ?? 'bg-surface-overlay text-text-muted'}`}>
                                                {actionTypeLabels[action.type] ?? action.type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-sm text-text-primary hidden sm:table-cell capitalize">
                                            {frequencyLabels[action.frequency] ?? action.frequency}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-text-muted">
                                            {formatDate(action.next_run_at)}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-text-muted hidden md:table-cell">
                                            {action.last_run_at ? formatDate(action.last_run_at) : '—'}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            {action.is_active ? (
                                                <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-status-paid-bg text-status-paid">
                                                    {t(KEYS.COMMON_YES)}
                                                </span>
                                            ) : (
                                                <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-surface-overlay text-text-muted">
                                                    {t(KEYS.COMMON_NO)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex justify-end gap-1">
                                                <button
                                                    onClick={() => handleViewRuns(action)}
                                                    className="p-2.5 sm:p-1.5 text-text-muted hover:text-accent rounded transition-colors"
                                                    title="View runs"
                                                >
                                                    <Eye className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(action)}
                                                    className="p-2.5 sm:p-1.5 text-text-muted hover:text-accent rounded transition-colors"
                                                    title="Edit"
                                                >
                                                    <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(action)}
                                                    className="p-2.5 sm:p-1.5 text-text-muted hover:text-status-overdue rounded transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                    <p className="text-sm text-text-secondary mb-4">{t(KEYS.SERVICE_ACTIONS_EMPTY)}</p>
                    <Button onClick={() => setIsAddModalOpen(true)} variant="primary" size="small">
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        {t(KEYS.SERVICE_ACTIONS_ADD)}
                    </Button>
                </div>
            )}

            {/* Modals */}
            <AddActionModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                serviceUid={uid}
            />
            <EditActionModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                action={selectedAction}
                serviceUid={uid}
            />
            <DeleteActionModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                action={selectedAction}
            />
            <ViewActionRunsModal
                isOpen={isRunsModalOpen}
                onClose={() => setIsRunsModalOpen(false)}
                action={selectedAction}
            />
        </div>
    );
};

export default ServiceActions;
